<template>
    <div
        class="w-full p-3 bg-white rounded-lg shadow justify-start items-start gap-6 grid grid-cols-12"
    >
        <div
            class="col-span-12 lg:col-span-3 relative"
            :class="{
                'rounded-lg': !property.image?.url,
            }"
        >
            <router-link
                v-if="property.slug"
                :to="{
                    name: 'HotelProperty',
                    params: { slug: property.slug },
                    query: { check_in: startDate, check_out: endDate },
                }"
            >
                <image-loader>
                    <template #image>
                        <img
                            loading="lazy"
                            :src="property.image?.url"
                            class="h-52 rounded-lg object-cover w-full min-w-52"
                        />
                    </template>
                    <template #preloader>
                        <div
                            class="min-w-52 h-52 rounded-lg bg-slate-200 shimmer"
                        ></div>
                    </template>
                    <template #error>
                        <div
                            class="min-w-52 h-52 rounded-lg object-cover flex items-center justify-center text-center flex-col bg-slate-200 text-slate-400"
                        >
                            <Icon icon="no-image" />
                            <!-- <p>Property image not available!</p> -->
                        </div>
                    </template>
                </image-loader>

                <div
                    class="h-9 p-2 bg-white bg-opacity-90 rounded justify-center items-center flex absolute bottom-2 right-2"
                >
                    <img
                        :src="property.propertyProgram.image"
                        class="max-w-[68px] h-auto max-h-7"
                    />
                </div>
            </router-link>
            <div class="w-8 h-8 absolute right-2 top-2">
                <Fav
                    :isWidget="false"
                    :indie="true"
                    :property="property"
                    shape="circle"
                />
            </div>
        </div>
        <div
            class="w-full flex-col justify-start items-start flex col-span-12 lg:col-span-9"
        >
            <div
                class="self-stretch pb-8 justify-start items-start flex flex-col lg:flex-row"
            >
                <router-link
                    v-if="property.slug"
                    :to="{
                        name: 'HotelProperty',
                        params: { slug: property.slug },
                        query: { check_in: startDate, check_out: endDate },
                    }"
                    class="grow shrink basis-0 flex-col justify-between lg:justify-start items-start gap-1 flex active:text-slate-500 focus:outline-none mb-5 lg:mb-0"
                >
                    <div
                        class="text-slate-800 text-base lg:text-xl font-medium leading-5 lg:leading-7 hover:text-slate-500"
                    >
                        {{ property.name }}
                    </div>
                    <div
                        class="text-slate-500 text-sm font-normal leading-tight"
                    >
                        {{ propertyAddress }}
                    </div>
                </router-link>
                <div
                    class="flex-row lg:flex-col justify-between lg:justify-start lg:items-end gap-1 flex self-stretch"
                >
                    <div
                        class="justify-start items-center lg:items-end gap-2 flex"
                    >
                        <div
                            class="lg:pb-[3px] justify-start items-end gap-2.5 flex"
                        >
                            <div
                                class="text-slate-500 text-xs font-medium leading-none"
                            >
                                {{ nightStayString }} total from
                            </div>
                        </div>
                        <div
                            class="text-slate-800 text-xl font-medium leading-7"
                        >
                            {{ cashPrice }}
                        </div>
                    </div>
                    <div
                        class="justify-start items-center lg:items-end gap-2 flex"
                    >
                        <div
                            class="lg:pb-[3px] justify-center items-center gap-2.5 flex"
                        >
                            <div
                                class="text-slate-500 text-xs font-medium leading-none"
                            >
                                {{
                                    property.group.name
                                        ? `${property.group.name} points from`
                                        : 'Points price from'
                                }}
                            </div>
                        </div>
                        <div
                            class="text-slate-800 text-xl font-medium leading-7"
                        >
                            {{ $filters.formatThousand(pointsPrice) }}
                        </div>
                    </div>
                </div>
            </div>
            <div
                class="self-stretch flex-col justify-start items-start flex relative"
            >
                <!-- START BOOKING INFO HEADER -->
                <div
                    class="self-stretch pb-3 justify-start items-center gap-6 flex"
                >
                    <div
                        class="grow shrink basis-0 text-slate-800 text-sm font-medium leading-tight"
                    >
                        Pay with points
                    </div>
                </div>
                <!-- END BOOKING INFO HEADER -->
                <!-- START PROPERTY CARD BOOKING INFO -->
                <PropertyCardBookingInfo
                    :propertyProgram="property.propertyProgram"
                    :perNightRate="property.min_points"
                    :urlParams="{
                        name: 'HotelProperty',
                        params: { slug: property.slug },
                        query: { check_in: startDate, check_out: endDate },
                    }"
                    layout="horizontal"
                />
                <!-- END PROPERTY CARD BOOKING INFO -->
            </div>
        </div>
    </div>
</template>
<script>
import { mapState } from 'pinia';
import { useHotelsStore } from '@/store/hotels';
import { useAppStore } from '@/store/app';
import { useCalendarStore } from '@/store/calendar';
import Fav from '@/components/hotels/property/fav.vue';
import Icon from '@/components/ui/icon/icon.vue';
import PropertyCardBookingInfo from '@/components/hotels/search/property-card/property-card-booking-info.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import Filters from '@/filters';

export default {
    name: 'PropertyCard',
    props: {
        property: {
            type: Object,
        },
        index: {
            type: Number,
        },
    },
    components: {
        Fav,
        PropertyCardBookingInfo,
        Icon,
        ImageLoader,
    },
    data: () => ({
        showAwardPriceDetails: false,
        showCashPriceDetails: false,
        isFavorite: null,
        minRate: 0,
        canAffordMessage: null,
    }),
    computed: {
        ...mapState(useAppStore, ['sidebarOpen']),
        ...mapState(useHotelsStore, ['page', 'perPage', 'stayLength']),
        ...mapState(useCalendarStore, ['startDate', 'endDate']),
        itemNumber() {
            const itemNumberOnPage = this.index + 1;
            if (this.page === 1) {
                return itemNumberOnPage;
            } else {
                const itemNumberOnAllPages =
                    (this.page - 1) * this.perPage + itemNumberOnPage;
                return itemNumberOnAllPages;
            }
        },

        nightStayString() {
            return `${this.stayLength} night${this.stayLength > 1 ? 's' : ''}`;
        },
        cashPrice() {
            return this.$filters.formatMoney(
                this.property.min_rate * this.stayLength
            );
        },
        pointsPrice() {
            return this.property.min_points * this.stayLength;
        },
        propertyAddress() {
            return Filters.formatAddress(this.property);
        },
    },
    methods: {},
};
</script>
